(() => {
  const colorValues = {
    ash: '#c3c4c6',
    aqua: '#57b8c8',
    berry: '#b12869',
    black: '#151515',
    bone: '#e8dec8',
    brown: '#704b37',
    burgundy: '#6f263d',
    butter: '#f1dfa1',
    caramel: '#a9663c',
    'carolina blue': '#7eafd2',
    chambray: '#a9c4d3',
    charcoal: '#45484c',
    cranberry: '#9d2c4c',
    'dark gray': '#494b4e',
    'dark gray heather': '#4b4f54',
    'dark heather gray': '#55575a',
    'dark heather grey': '#55575a',
    'dark grey': '#494b4e',
    'dark grey heather': '#4b4f54',
    'dark heather': '#48494d',
    evergreen: '#29483f',
    forest: '#294a35',
    'forest green': '#294a35',
    gold: '#d8a72f',
    granite: '#696c70',
    gray: '#85878a',
    green: '#35734b',
    'green camo': '#596143',
    grey: '#85878a',
    'grey melange': '#b4b5b6',
    graphite: '#66676a',
    heather: '#929396',
    'heather gray': '#929396',
    'heather grey': '#929396',
    'heather mauve': '#a16f78',
    'heather navy': '#33445d',
    'heather slate': '#4f6871',
    'indigo blue': '#3e617c',
    ivory: '#f5eedc',
    khaki: '#b5a77f',
    'light blue': '#b5d8e8',
    'light green': '#a4c9a3',
    'light washed denim': '#93aabd',
    loden: '#50583d',
    maroon: '#6c2939',
    mauve: '#a16f78',
    'military green': '#5d6445',
    moss: '#687052',
    'mouse grey': '#70706f',
    natural: '#e7ddc8',
    navy: '#1c2c49',
    'navy blazer': '#202a3a',
    olive: '#656545',
    orange: '#d7672f',
    pink: '#dc8299',
    purple: '#654b78',
    red: '#c93442',
    royal: '#3158a6',
    'royal blue': '#3158a6',
    'rustic orange': '#a65332',
    'safety green': '#c6d62f',
    sand: '#d4c09e',
    scarlet: '#d71920',
    seafoam: '#a8d7c7',
    silver: '#b9babd',
    'sky blue': '#b4dce9',
    'solid black blend': '#151515',
    'solid white blend': '#ffffff',
    'sport gray': '#a5a5a4',
    'sport grey': '#a5a5a4',
    spruce: '#25483f',
    stone: '#c8bda8',
    teal: '#2f7c78',
    'true navy': '#17253f',
    turquoise: '#43cfc9',
    'vintage white': '#f2ede1',
    'washed black': '#3f4143',
    white: '#ffffff',
    yellow: '#e8c33b',
  };

  const normalizeColorName = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');

  const solidColorFor = (value) => {
    const normalized = normalizeColorName(value);
    if (colorValues[normalized]) return colorValues[normalized];

    const compact = normalized.replace(/\s+/g, '');
    if (globalThis.CSS?.supports?.('color', compact)) return compact;
    return '#a8aaad';
  };

  const backgroundFor = (value) => {
    const parts = String(value || '')
      .split(/\s*(?:\/|&|\+)\s*/)
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length < 2) return solidColorFor(value);

    const segment = 100 / parts.length;
    const stops = parts.flatMap((part, index) => {
      const color = solidColorFor(part);
      return [`${color} ${segment * index}%`, `${color} ${segment * (index + 1)}%`];
    });
    return `linear-gradient(135deg, ${stops.join(', ')})`;
  };

  const decorate = (element, value) => {
    element.style.setProperty('--swatch-background', backgroundFor(value));
    element.dataset.colorSwatch = value;
  };

  window.FerryColorSwatches = Object.freeze({ backgroundFor, decorate });

  const safeDecode = (value) => {
    try {
      return decodeURIComponent(value);
    } catch (error) {
      return value;
    }
  };

  const mediaMetadata = (media) => {
    const filename = safeDecode(String(media.src || '').split('?')[0].split('/').pop() || '');
    const viewMatch = filename.match(/^(.*)-(front|left|right|back)-.+\.[^.]+$/i);
    return {
      ...media,
      groupKey: viewMatch?.[1] || '',
      view: viewMatch?.[2]?.toLowerCase() || '',
    };
  };

  const initializeProductCard = (card) => {
    const dataElement = card.querySelector('[data-product-card-data]');
    const swatches = Array.from(card.querySelectorAll('[data-product-card-swatch]'));
    if (!dataElement || swatches.length === 0) return;

    let data;
    try {
      data = JSON.parse(dataElement.textContent);
    } catch (error) {
      return;
    }

    const colorOptionIndex = Number.parseInt(data.colorOptionIndex, 10);
    const variants = Array.isArray(data.variants) ? data.variants : [];
    const media = (Array.isArray(data.media) ? data.media : []).map(mediaMetadata);
    const mediaById = new Map(media.map((item) => [String(item.id), item]));
    const primaryImage = card.querySelector('[data-product-card-primary-image]');
    const hoverImage = card.querySelector('[data-product-card-hover-image]');
    const mediaLink = card.querySelector('[data-product-card-media]');
    const links = Array.from(card.querySelectorAll('[data-product-card-link]'));
    const status = card.querySelector('[data-product-card-color-status]');
    const isVisibleSwatch = (swatch) => !swatch.disabled && swatch.getClientRects().length > 0;

    if (!Number.isInteger(colorOptionIndex) || !primaryImage || !mediaLink || links.length === 0) return;

    const updateImage = (image, item, colorName) => {
      if (!image || !item) return;
      image.src = item.src;
      image.removeAttribute('srcset');
      image.alt = item.alt || `${data.title || 'Product'} in ${colorName}`;
    };

    const selectSwatch = (button, { focus = false } = {}) => {
      if (button.disabled) return;
      const colorName = button.dataset.optionValue;
      const colorVariants = variants.filter((variant) => variant.options?.[colorOptionIndex] === colorName);
      const availableVariants = colorVariants.filter((variant) => variant.available);
      const mediaIdCounts = colorVariants.reduce((counts, variant) => {
        const mediaId = String(variant.mediaId || '');
        if (mediaById.has(mediaId)) counts.set(mediaId, (counts.get(mediaId) || 0) + 1);
        return counts;
      }, new Map());
      const representativeMediaId =
        [...mediaIdCounts.entries()].sort((first, second) => second[1] - first[1])[0]?.[0] || '';
      const representativeVariant =
        availableVariants.find((variant) => String(variant.mediaId || '') === representativeMediaId) ||
        availableVariants[0] ||
        colorVariants[0];
      const representativeMedia = mediaById.get(representativeMediaId);
      const colorMedia = representativeMedia?.groupKey
        ? media.filter((item) => item.groupKey === representativeMedia.groupKey)
        : representativeMedia
          ? [representativeMedia]
          : [];
      const primaryMedia = colorMedia.find((item) => item.view === 'front') || representativeMedia || colorMedia[0];
      const hoverMedia =
        colorMedia.find((item) => item.view === 'back') || colorMedia.find((item) => item.id !== primaryMedia?.id);

      if (primaryMedia) updateImage(primaryImage, primaryMedia, colorName);

      if (hoverImage && hoverMedia) {
        updateImage(hoverImage, hoverMedia, colorName);
        hoverImage.hidden = false;
        mediaLink.classList.add('product-card__media--has-hover');
      } else if (hoverImage) {
        hoverImage.hidden = true;
        mediaLink.classList.remove('product-card__media--has-hover');
      }

      if (representativeVariant?.id) {
        links.forEach((link) => {
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('variant', representativeVariant.id);
          link.href = url.toString();
        });
      }

      swatches.forEach((swatch) => swatch.setAttribute('aria-pressed', String(swatch === button)));
      card.dataset.selectedColor = colorName;
      if (status) status.textContent = `${colorName} selected`;
      if (focus) button.focus();
    };

    swatches.forEach((button) => {
      const colorName = button.dataset.optionValue;
      decorate(button, colorName);
      const isAvailable = variants.some(
        (variant) => variant.available && variant.options?.[colorOptionIndex] === colorName
      );
      button.disabled = !isAvailable;
      button.title = isAvailable ? colorName : `${colorName} — Unavailable`;
      button.setAttribute('aria-label', isAvailable ? `Preview ${colorName}` : `${colorName} — Unavailable`);
      button.addEventListener('click', () => {
        selectSwatch(button);
        button.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
      button.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const available = swatches.filter(isVisibleSwatch);
        if (available.length === 0) return;
        event.preventDefault();
        const currentIndex = Math.max(0, available.indexOf(button));
        let nextIndex = currentIndex;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = available.length - 1;
        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + available.length) % available.length;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % available.length;
        selectSwatch(available[nextIndex], { focus: true });
        available[nextIndex].scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    });

    const initialSwatch = swatches.find(isVisibleSwatch) || swatches.find((button) => !button.disabled);
    if (initialSwatch) selectSwatch(initialSwatch);

    const compactCardQuery = window.matchMedia('(max-width: 449px)');
    const ensureSelectedSwatchIsVisible = () => {
      const selectedSwatch = swatches.find((button) => button.getAttribute('aria-pressed') === 'true');
      if (!selectedSwatch || selectedSwatch.getClientRects().length > 0) return;
      const visibleSwatch = swatches.find(isVisibleSwatch);
      if (visibleSwatch) selectSwatch(visibleSwatch);
    };
    compactCardQuery.addEventListener?.('change', ensureSelectedSwatchIsVisible);
  };

  document.querySelectorAll('[data-product-card]').forEach(initializeProductCard);
})();
